/**
 * The ground HUD's view (67.35; lane E's mock, design_sheets/hud.html, approved by the owner): what the HUD shows, as
 * plain data, from the match state and the few things only the host knows (the seated gun's dial, the aim's state, the
 * hit and hurt timers, the start of the hold). Pure: tests read it; `GroundHud` (groundHud.ts) draws it. The regions,
 * as the mock's rules have them:
 *
 * - **always** while you hold a body: the match (top), you (bottom left), what you hold (bottom right), the crosshair,
 *   the corner map;
 * - **per match**, fixed when the match starts and never popping in or out: the capture points, the bandages, the carried
 *   kit (grenades, drones and charges, the beacon — a zero dimmed);
 * - **when relevant**: the one slot under the crosshair (a prompt, or a timed action's bar), the message, the keys card
 *   (open for the first seconds and while ? is held), the feed.
 *
 * Keys come from the one table (l0Keys.ts) by binding id; the HUD never names a key itself.
 */
import { isSupport, caseLeft } from '../core/supportRole';
import { isEngineer, chargesLeft } from '../core/charges';
import { assembling } from '../core/convoyMarch';
import { coverMark, type CoverHeld, type CoverMark } from './coverCue';
import type { BuildKind, GameState, PlayerState, Vec3 } from '../types';
import { TEAMS } from '../constants';
import { personSetOf } from '../core/scaleProfile';
import { weaponSpec, weaponOf, roundsLeft, reserveLeft, mountedGunSpec } from '../core/weapons';
import { bandagesLeft, healProgress } from '../core/medical';
import { stockLeft, explosivesLeft } from '../core/supplyStock';
import { beaconsLeft } from '../core/beacon';
import { buildSeconds, plantingKind, buildOrderKind, buildOn, buildsLeft, buildReach, canBuild, spawnSiteRefusal, frontSiteKind } from '../core/build';
import { squadTaskFor, squadOf } from '../core/squads';
import { costsOn, paidAtPlanting, buildCost, storeFor } from '../core/buildCosts';
import { nearestTeamCrate, supplyReach } from '../core/supply';
import { servesPool } from '../core/fob';
import { arcDistance } from '../core/gameState';
import { PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { BEACON, RELAY, PHYS_DT, MOUNT_LAUNCHERS, MORTAR, STORY, CHARGE } from '../constants';
import { mortarSetupSeconds } from '../core/mortar';
import { mainGunReload } from '../core/crewman';
import { targetsLeft } from '../core/storyObjectives';
import { crateInReach, supplyNeeds } from '../core/supply';
import { windAt } from '../core/wind';
import { cellsAt, weatherTime, type WindCell } from '../core/windField';
import type { WeatherKind } from '../core/weather';
import { headingOf, rightOf } from '../core/surfaceWalk';
import { vecNormalize, uprightFrame } from '../math';
import { G } from '../constants';

/**
 * 67.35 + 62.52: the wind cue — the wind where you stand, against your view: `bearing` the way it blows, in degrees
 * clockwise from straight ahead (0: blowing the way you look, 180: in your face); `strength` 0–1 (a storm's push is 1);
 * `words` calm · light · fresh · strong · a gale. From the wind's push as a share of the planet's surface gravity, its
 * part along the ground. Null with the match's wind switch off. Pure.
 */
export interface WindCue { bearing: number; strength: number; words: string }
/** The push (a share of surface gravity) that reads as full strength: a storm's (core/weather.ts WEATHER_EFFECTS). */
export const WIND_FULL_SHARE = 0.25;
export function windCue(wind: Vec3 | null, up: Vec3, heading: Vec3, gravity: number): WindCue {
  const calm = { bearing: 0, strength: 0, words: 'calm' };
  if (!wind || !(gravity > 0)) return calm;
  const u = Math.hypot(up.x, up.y, up.z) || 1, n = { x: up.x / u, y: up.y / u, z: up.z / u };
  const a = wind.x * n.x + wind.y * n.y + wind.z * n.z;
  const h = { x: wind.x - n.x * a, y: wind.y - n.y * a, z: wind.z - n.z * a };   // its part along the ground
  const share = Math.hypot(h.x, h.y, h.z) / gravity;
  if (share < 0.005) return calm;
  const right = { x: heading.y * n.z - heading.z * n.y, y: heading.z * n.x - heading.x * n.z, z: heading.x * n.y - heading.y * n.x };   // heading × up
  const fwd = h.x * heading.x + h.y * heading.y + h.z * heading.z, side = h.x * right.x + h.y * right.y + h.z * right.z;
  const bearing = ((Math.atan2(side, fwd) * 180) / Math.PI + 360) % 360;
  const strength = Math.min(1, share / WIND_FULL_SHARE);
  const words = strength < 0.12 ? 'light' : strength < 0.35 ? 'fresh' : strength < 0.7 ? 'strong' : 'a gale';
  return { bearing, strength, words };
}

/**
 * The wind where a person stands now: lane A's generator (62.47 / 62.52, core/wind.ts windAt — the planet's gusting wind
 * × the shelter where they stand), the same wind their shots fly through, so the cue gusts and drops in shelter.
 */
export function windAtPerson(state: GameState, p: PlayerState, seconds: number): Vec3 | null {
  return windAt(state, p.planetIndex, p.surfaceDir, seconds);
}

/**
 * 62.54 step 7: what weather is arriving (with the weather cells on) — the nearest cell of rain, storm, fog, dust or ash
 * whose edge is coming toward you, its bearing against your view (degrees clockwise from ahead) and the seconds until
 * its edge reaches you (from the cells now and `AHEAD_S` on). Null when none is coming within `ARRIVAL_WITHIN_S`. Pure.
 */
export interface Arrival { kind: WeatherKind; bearing: number; seconds: number }
export const ARRIVAL_WITHIN_S = 180, AHEAD_S = 10;
const ARRIVING: ReadonlySet<WeatherKind> = new Set(['rain', 'snow', 'storm', 'fog', 'dust', 'ash']);
export function arrivalFrom(now: readonly WindCell[], soon: readonly WindCell[], up: Vec3, heading: Vec3, dt = AHEAD_S): Arrival | null {
  const n = Math.hypot(up.x, up.y, up.z) || 1, u = { x: up.x / n, y: up.y / n, z: up.z / n };
  const edge = (c: WindCell) => Math.acos(Math.max(-1, Math.min(1, u.x * c.centre.x + u.y * c.centre.y + u.z * c.centre.z))) - c.radius;
  let best: Arrival | null = null;
  now.forEach((c, i) => {
    const later = soon[i];
    if (!ARRIVING.has(c.kind) || !later || later.kind !== c.kind) return;
    const e0 = edge(c), e1 = edge(later);
    if (e0 <= 0 || !(e1 < e0)) return;   // overhead (you are in it) or not coming
    const seconds = e0 / ((e0 - e1) / dt);
    if (seconds > ARRIVAL_WITHIN_S || (best && seconds >= best.seconds)) return;
    const cosA = u.x * c.centre.x + u.y * c.centre.y + u.z * c.centre.z;
    const t = { x: c.centre.x - u.x * cosA, y: c.centre.y - u.y * cosA, z: c.centre.z - u.z * cosA };
    const right = { x: heading.y * u.z - heading.z * u.y, y: heading.z * u.x - heading.x * u.z, z: heading.x * u.y - heading.y * u.x };
    const fwd = t.x * heading.x + t.y * heading.y + t.z * heading.z, side = t.x * right.x + t.y * right.y + t.z * right.z;
    best = { kind: c.kind, bearing: ((Math.atan2(side, fwd) * 180) / Math.PI + 360) % 360, seconds };
  });
  return best;
}

/** How long the keys card stays open after you take a body (seconds). */
export const KEYS_OPEN_S = 8;

export type CrosshairState = 'aim' | 'far' | 'friend' | 'blocked' | 'hidden';
export type KitId = 'grenadeHE' | 'grenadeSmoke' | 'drone' | 'charge' | 'beacon' | 'ammoCase' | 'demoCharge';   // 69.45: the support's case; 69.47: the engineer's charges (`charge` is the drone's payload)

/** What only the host knows, each frame. */
export interface GroundHudHost {
  /** the body you hold (null: watching) */
  viewerId: number | null;
  /** 65.28: seconds since a round last came near you (the Match's `nearMiss` event for your unit; absent: none yet) — the cue's pulse */
  sinceNearMiss?: number;
  /** seconds since the match began (the match clock: the weather's and the wind's time), and since you took this body (the keys card's first seconds) */
  time: number;
  heldSince: number;
  /** ? held: the keys card open */
  helpHeld?: boolean;
  /** in the commander's view (L1): its own line teaches its keys, so the keys card steps aside */
  commanding?: boolean;
  /** the seated gun's dial (host state): its shell and its power 0–100 */
  gun?: { shell: string; power: number };
  /** riding: speed (people a second) and throttle 0–1 */
  ride?: { speed: number; throttle: number };
  /** the crosshair's state (the aim's: in reach, a friend in the line, blocked at the muzzle) */
  crosshair: CrosshairState;
  hit?: boolean;
  /** you were hurt a moment ago (the red at the screen's edge) */
  hurt?: boolean;
  /** a hit from this bearing (degrees clockwise from ahead) is still showing */
  hurtFrom?: number;
  /** the teammate you look at who would take a bandage (actionHost's medicTarget) */
  medicTarget?: Pick<PlayerState, 'name'> | null;
  /** a short note after an action (a refusal, "bandaged") and the message in the middle (down, click to play) */
  note?: string;
  message?: { title: string; sub: string };
  /** seconds left in the match, when it has a limit */
  secondsLeft?: number | null;
  /** the corner map shown (its toggle) */
  mapShown: boolean;
  /** 68.3b: the squad strip's lines, from the host's cards (core/squadCards.ts) — empty without squads, at L1, watching */
  squads?: StripLine[];
  /** 66.7 (a): the cover you are in (ui/coverCue.ts; the Match's PlayerState.cover) — the crosshair's bracket */
  coverHeld?: CoverHeld | null;
  /** 63.8: the player's gait now (the host's): the move (the gun down — its first click unaimed) or the sprint (no fire); absent: the walk or still. */
  gait?: 'move' | 'sprint';
  /** 63.1d's follow-up: the seat E gets into here (its name: driver, gunner, bay, ride), from actionHost's seatOffer */
  seatOffer?: string | null;
}

/** 68.3b: a squad on the strip — its number (the keys reach 1–4), name, the members as last reported (a pip each, the
 * leader's marked), its status as a marker glyph, its order as an icon and words, how fresh the report is, and whether
 * the order menu (F) orders it now. */
export interface StripLine {
  number: number; label: string; own: boolean; selected: boolean; spent: boolean;
  members: Array<{ alive: boolean; health: number; leader: boolean }>;
  marker: 'contact' | 'moving' | 'holding' | 'scouting' | 'crewing' | 'withdrawing' | 'hurt';
  orderIcon: 'go' | 'hold' | 'follow' | 'take' | 'engage' | 'crew' | 'tactics' | 'withdraw' | 'mark' | 'formation' | 'callFire' | null;
  order: string;
  freshness: 'fresh' | 'stale' | 'outOfSight';
  age: number | null;
}

export interface GroundHudView {
  match: {
    teams: Array<{ name: string; color: string; tickets: number | null; alive: number; mine: boolean }>;
    clock: string | null;
    points: Array<{ letter: string; color: string; taking: { color: string; progress: number } | null; here: boolean }>;
    /** 69.50 step 1: the story's objectives (GameConfig.storyObjectives): yours first, then the other side's — each its words, how far along (0 … 1), done; absent while off */
    objectives?: Array<{ text: string; progress: number; done: boolean; mine: boolean }>;
  };
  you: { name: string; health: number; stance: 'stand' | 'crouch' | 'prone'; bandages: number | null; down: boolean; /** DECISIONS 402 (finiteSupply): what your side's containers still hold, and whether that is under a few issues a unit */ stock?: { magazines: number; bandages: number; /** 406: grenades, 40 mm rounds and rockets */ explosives: number; low: boolean }; armor?: number; /** 185: a helmet worn, and whether it is spent */ helmet?: 'whole' | 'spent' } | null;
  hands: {
    weapon?: { kind: string; rounds: number; mag: number; spare?: number; status?: string };
    gun?: { shell: string; power: number; status: string; /** 63.1f's follow-up: a gun pit's bearing against its front ('40° right', 'ahead') */ traverse?: string };
    ride?: { speed: number; throttle: number };
    kit: Array<{ id: KitId; key: string | null; count: number; verb: string }>;
    /** 67.83 (the owner: "Would be nice to get some indication of reload progress for all weapons light and turret"): each reload under way — what reloads, and its share done (0…1); `doing` what fills when not a reload (the mortar's setting up, its next round) */
    reloads?: Array<{ label: string; progress: number; doing?: string }>;
  } | null;
  /** the one slot under the crosshair: a timed action's bar, else a prompt (the nearer thing's) */
  /** 62.54 step 7: the weather arriving and from where (null: none coming, or the weather cells off) */
  arrival: Arrival | null;
  timed: { text: string; progress: number } | null;
  /** 66.8: the match digs (the Lab's Digging in) — the keys card shows the dig. */
  digOn?: boolean;
  prompt: { key: string; verb: string; detail?: string } | null;
  keysCard: 'open' | 'closed' | 'none';
  crosshair: CrosshairState;
  hit: boolean;
  hurt: boolean;
  hurtFrom: number | null;
  /** 65.28 (decision 161, GameConfig.suppression): rounds passing near you — the seconds left on the mark (the dark at the
   *  screen's edge eases out as it runs down; a cue only) */
  suppressed?: number;
  /** 65.28: seconds since a round last came near you (the host's, from the `nearMiss` event): the cue's pulse (ui/suppressionCue.ts) */
  sinceNearMiss?: number;
  note: string | null;
  message: { title: string; sub: string };
  mapShown: boolean;
  /** 68.3b: the squad strip (the left column, above your card) */
  squads: StripLine[];
  /** 66.7 (a): in cover — the cover's side against the crosshair, and whether you are out of it (null: not in cover) */
  cover: CoverMark | null;
  /** 63.8: the gun down (the move) or the sprint — the crosshair drawn open and dim, or greyed. */
  gait?: 'move' | 'sprint';
}

/** Each carried item: its key (a binding id in l0Keys.ts; the charge has none of its own — the tablet drops it) and its verb. */
const KIT_BIND: Record<KitId, { key: string | null; verb: string }> = {
  grenadeHE: { key: 'he', verb: 'throw HE' }, grenadeSmoke: { key: 'smoke', verb: 'smoke' }, drone: { key: 'tablet', verb: 'tablet' },
  charge: { key: null, verb: 'by tablet' }, beacon: { key: 'beacon', verb: 'beacon' }, ammoCase: { key: 'ammoCase', verb: 'case' },
  demoCharge: { key: 'demoCharge', verb: 'charge' },
};

const clockOf = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** The HUD's view of `state` for the host's frame. */
/** 69.2f: what the planter is making, said on the timed slot. */
const PLANTING: Record<BuildKind, string> = { beacon: 'planting the beacon', relay: 'planting a relay', fob: 'setting up a FOB', hmg: 'emplacing an HMG' };
/** 69.2g's follow-up: a build, named for the prompt slot (as the planting bar names it). */
const BUILD_VERB: Record<BuildKind, string> = { beacon: 'plant the beacon', relay: 'plant the relay', fob: 'set up the FOB', hmg: 'emplace the HMG' };
const NOUN: Record<BuildKind, string> = { beacon: 'beacon', relay: 'relay', fob: 'FOB', hmg: 'HMG kit' };

/**
 * 69.2g's follow-up (the owner thought E builds; it does not — a planting starts by itself at the spot): what the prompt slot
 * says of the unit's own build task. Out of reach, how far the spot is; at the spot and not planting, what holds the start
 * (nothing to plant with — a crate's E restocks relays and the beacon; fire or a jump; no store in reach to pay; a spot the
 * front has passed). Null with no build task, or when the planting should start (it is about to: the bar takes the slot).
 */
export function buildPrompt(state: GameState, me: PlayerState, tick: number): { key: string; verb: string; detail?: string } | null {
  const task = squadTaskFor(state, me.id, tick);
  const kind = task ? buildOrderKind(task.order) : null;
  if (!task || !kind || !buildOn(state.config, kind) || me.plantLeft !== undefined) return null;
  const spot = task.order as { planetIndex: number; surfaceDir: Vec3 };
  const planet = state.planets[me.planetIndex];
  if (!planet || spot.planetIndex !== me.planetIndex) return null;
  const verb = BUILD_VERB[kind], person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const off = arcDistance(me.surfaceDir, spot.surfaceDir, planet.radius) - buildReach(state, kind);
  if (off > 0) return { key: '', verb, detail: `${Math.max(1, Math.round(off / person))} people away` };
  if (buildsLeft(state, me, kind) <= 0) return { key: '', verb, detail: kind === 'relay' || kind === 'beacon' ? `no ${NOUN[kind]} in hand · E at a crate` : `no ${NOUN[kind]} left` };
  const fire = state.intents?.[me.id]?.fire === true;
  if (fire) return { key: '', verb, detail: 'hold your fire to plant' };
  if (frontSiteKind(kind, state.config)) { const why = spawnSiteRefusal(state, me.teamIndex, me); if (why) return { key: '', verb, detail: why }; }
  if (costsOn(state.config) && paidAtPlanting(kind) && !storeFor(state, me.teamIndex, me, buildCost(kind), kind)) return { key: '', verb, detail: 'no store in reach can pay' };
  if (!canBuild(state, me, kind, spot)) return { key: '', verb, detail: 'stand still, hands free' };
  return null;
}

/** 69.2g's follow-up (a): the relays and the beacon E would take from a crate in reach (the side's kits, the squad short). */
export function kitsAtCrate(state: GameState, me: PlayerState): string | null {
  if (!state.buildKits || !me.alive || me.mount || me.airborne) return null;
  const sq = squadOf(state, me.id), kit = state.buildKits[me.teamIndex];
  if (!kit) return null;
  const relays = state.config.relays && sq && sq.relays !== undefined ? Math.min(kit.relay, Math.max(0, RELAY.carried - sq.relays)) : 0;
  const beacon = state.config.beacons && sq && Math.min(...sq.unitIds) === me.id ? Math.min(kit.beacon, Math.max(0, BEACON.carried - (me.beacons ?? 0))) : 0;
  if (relays <= 0 && beacon <= 0) return null;
  if (!nearestTeamCrate(state, me, supplyReach(state), x => x.health > 0 && servesPool(state, x))) return null;
  return [relays > 0 ? `${relays} relay${relays > 1 ? 's' : ''}` : '', beacon > 0 ? 'the beacon' : ''].filter(Boolean).join(', ');
}

export function groundHudView(state: GameState, host: GroundHudHost): GroundHudView {
  const me = host.viewerId === null ? null : state.players.find(p => p.id === host.viewerId) ?? null;
  const tickets = state.modeState.respawn?.tickets;
  const teamIds = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const mineFirst = me ? [me.teamIndex, ...teamIds.filter(t => t !== me.teamIndex)] : teamIds;
  const match: GroundHudView['match'] = {
    teams: mineFirst.map(t => ({
      name: TEAMS[t % TEAMS.length].name.split(/[\s&]+/)[0], color: TEAMS[t % TEAMS.length].primary,
      tickets: tickets ? tickets[t] ?? 0 : null, alive: state.players.filter(p => p.alive && p.teamIndex === t).length, mine: me?.teamIndex === t,
    })),
    clock: host.secondsLeft === null || host.secondsLeft === undefined ? null : clockOf(Math.max(0, host.secondsLeft)),
    points: (state.modeState.points?.sites ?? []).map((site, i) => ({
      letter: String.fromCharCode(65 + i),
      color: site.owner === null ? '#c9ccd2' : TEAMS[site.owner % TEAMS.length].primary,
      taking: site.capturer !== null && site.progress > 0 ? { color: TEAMS[site.capturer % TEAMS.length].accent, progress: site.progress } : null,
      here: false,
    })),
    ...(state.modeState.objectives ? { objectives: objectiveLines(state, me?.teamIndex ?? null) } : {}),
  };
  const base = {
    match, timed: null, prompt: null, note: host.note ?? null, message: host.message ?? { title: '', sub: '' }, mapShown: host.mapShown, squads: host.squads ?? [],
    hit: host.hit === true, hurt: host.hurt === true || host.hurtFrom !== undefined, hurtFrom: host.hurtFrom ?? null,
    ...((me?.suppressed ?? 0) > 0 ? { suppressed: me!.suppressed } : {}),
    ...(host.sinceNearMiss !== undefined ? { sinceNearMiss: host.sinceNearMiss } : {}),
  };
  if (!me) return { ...base, arrival: null, you: null, hands: null, keysCard: 'none', crosshair: 'hidden', cover: null };
  const planet = state.planets[me.planetIndex];
  const cellsOn = (state.config as typeof state.config & { weatherCells?: boolean }).weatherCells === true;
  const arrival = cellsOn && planet ? arrivalFrom(cellsAt(state, me.planetIndex, weatherTime(state, host.time)), cellsAt(state, me.planetIndex, weatherTime(state, host.time + AHEAD_S)), me.surfaceDir, headingOf(me)) : null;

  const config = state.config, ps = personSetOf(state);
  const medical = config.medical !== false;
  const you: NonNullable<GroundHudView['you']> = {
    name: me.name, health: Math.max(0, Math.min(100, me.health)), stance: me.stance === 'crouch' ? 'crouch' : me.stance === 'prone' ? 'prone' : 'stand',
    bandages: medical ? bandagesLeft(me) : null, down: !me.alive,
    ...(state.supplyStocks?.[me.teamIndex] ? { stock: sideStock(state, me.teamIndex) } : {}),
    // 185: the vest's life left (the armour bar), and the helmet's mark
    ...(me.armour?.vest ? { armor: Math.max(0, Math.min(100, (100 * me.armour.vest.life) / me.armour.vest.max)) } : {}),
    ...(me.armour?.helmet ? { helmet: me.armour.helmet.life > 0 ? 'whole' as const : 'spent' as const } : {}),
  };

  // what you hold: the gun you sit at, the ride, or the weapon in hand (its rounds, the magazine, its spares, a draw or a reload)
  const hands: NonNullable<GroundHudView['hands']> = { kit: [] };
  const body = me.mount ? state.structures.find(s => s.id === me.mount!.bodyId) : undefined, seat = body?.seats?.[me.mount!.seat];
  if (seat?.main) {
    // 249 (the owner: "gunner / commander seems to be the same slot, with only one weapon"): a tank's turret — the main gun
    // (the round in it, those of its kind stowed), the round next and the coax's belt beside it (right mouse)
    const main = seat.main, kind = main.loaded ?? main.next, word = (r: 'he' | 'solid') => (r === 'he' ? 'HE' : 'SOLID');
    const coax = seat.guns?.[0] ? seat.belt ?? mountedGunSpec(ps, seat.guns[0]).magazine : null;
    hands.weapon = { kind: `main ${word(kind)}`, rounds: main.loaded ? 1 : 0, mag: 1, spare: main[kind], status: `${main.reloadLeft !== undefined ? 'loading · ' : ''}next ${word(main.next)}${coax !== null ? ` · coax ${coax}` : ''}` };
  } else if (seat?.guns?.length) {
    // 63.1d step 3: a mounted bullet gun — its belt as a weapon's rounds
    const g = mountedGunSpec(ps, seat.guns[0]);
    hands.weapon = { kind: seat.guns[0], rounds: seat.belt ?? g.magazine, mag: g.magazine, ...(seat.reloadLeft !== undefined ? { status: 'reloading' } : {}) };
  } else if (seat?.launcher) {
    // fix/launcher-seat-hud: a mounted launcher (the rocket technical's tubes, the missile technical's rail) — the rounds ready
    // in its tubes (a reload's are counted in when it starts, but none fire till it ends), its store's left to reload from (it
    // read the hand weapon's, which a seat never fires)
    const L = MOUNT_LAUNCHERS[seat.launcher];
    hands.weapon = { kind: seat.launcher === 'rocket' ? 'rockets' : 'missile', rounds: seat.reloadLeft !== undefined ? 0 : seat.belt ?? L.magazine, mag: L.magazine, spare: seat.spare ?? config.mountStore ?? L.store, ...(seat.reloadLeft !== undefined ? { status: 'reloading' } : {}) };
  } else if (seat?.mortar && body?.mortar) {
    // fix/launcher-seat-hud: the mortar's crew — its store (a call takes a mission's rounds from it), and where the tube stands:
    // moving, setting up, set for a call, firing a mission
    const tick = Math.round(host.time / PHYS_DT), m = body.mortar, due = mortarRoundsDue(state, body.id, tick).length;
    const status = due ? `firing · ${due} to go` : m.rounds <= 0 ? 'store empty' : m.stillSince === undefined ? 'moving' : (tick - m.stillSince) * PHYS_DT < mortarSetupSeconds(state, body) ? 'setting up' : 'set · on call';
    hands.weapon = { kind: 'mortar', rounds: m.rounds, mag: config.mortarStore ?? MORTAR.store, status };
  } else if (seat && host.gun) {
    const traverse = pitTraverse(state, me);
    hands.gun = { shell: host.gun.shell, power: Math.round(host.gun.power), status: seat.reloadLeft !== undefined ? `reloading ${seat.reloadLeft.toFixed(1)} s` : 'ready', ...(traverse ? { traverse } : {}) };
  }
  else if (host.ride) hands.ride = host.ride;
  else if (config.groundWeapons !== false) {
    const kind = weaponOf(me), w = weaponSpec(ps, kind, config);   // (the owner, Oct 13: an LMG read "30/30" then "99/30" — the spec was the rifle's while `weapon` was unset for the primary)
    hands.weapon = {
      kind, rounds: roundsLeft(me, w), mag: w.magazine,
      ...(w.reserve !== undefined ? { spare: reserveLeft(me, kind, w) } : {}),
      ...(me.switchLeft !== undefined ? { status: 'drawing' } : me.reloadLeft !== undefined ? { status: 'reloading' } : {}),
    };
  }
  // 67.83: the reloads under way, each filling — the weapon in hand, the seat's gun (its belt, the tank's main gun, a launcher's
  // tubes, a gun pit's shell) and the mortar (setting up, the next round of a mission)
  const reloads = reloadsOf(state, me, host.time);
  if (reloads.length) hands.reloads = reloads;
  // the kit row: per match — an item the match gives shows all match, a zero dimmed
  const kit = (id: KitId, count: number) => hands.kit.push({ id, key: KIT_BIND[id].key, count, verb: KIT_BIND[id].verb });
  if (me.grenades) { kit('grenadeHE', me.grenades.he); kit('grenadeSmoke', me.grenades.smoke); }
  if (me.payloadIssue) { kit('drone', me.drones ?? 0); kit('charge', me.charges ?? 0); }
  if (config.beacons === true) kit('beacon', beaconsLeft(me));
  if (isEngineer(state, me)) kit('demoCharge', chargesLeft(state, me));   // 69.47 step 1: the engineer's charges
  if (isSupport(state, me)) { kit('ammoCase', caseLeft(me)); if (me.caseDown !== undefined) hands.kit[hands.kit.length - 1].verb = 'case down'; }   // 69.45: its magazines (0 while set down)

  // the one slot: a timed action first (planting the beacon, bandaging, being bandaged), else the nearer prompt
  let timed: GroundHudView['timed'] = null, prompt: GroundHudView['prompt'] = null;
  const patient = me.treating !== undefined ? state.players.find(q => q.id === me.treating) : undefined;
  // 69.2f (the owner: "I can't build stuff as a player … "planting the beacon" bar shows up but never progresses"): the bar named
  // every build the beacon and ran it on the beacon's 3 s — a relay's 5 s, a FOB's 20 s showed empty till their last 3 s (the
  // sim planted all along: yielded to the bot, the same planting finished). Each build by its own name and time now
  if (me.chargeLeft !== undefined) timed = { text: me.chargeFuse === 'wire' ? 'setting a charge on the wire' : 'setting a charge on its timer', progress: Math.max(0, Math.min(1, 1 - me.chargeLeft / CHARGE.kneelSeconds)) };   // 69.47 step 1
  else if (me.plantLeft !== undefined) { const kind = plantingKind(me); timed = { text: PLANTING[kind], progress: Math.max(0, Math.min(1, 1 - me.plantLeft / buildSeconds(kind))) }; }
  else if (patient) timed = { text: `bandaging ${patient.name}`, progress: healProgress(patient) ?? 0 };
  else if (me.healLeft !== undefined) {
    const by = me.healBy !== undefined ? state.players.find(q => q.id === me.healBy)?.name ?? 'a teammate' : null;
    timed = { text: by ? `bandaged by ${by}` : 'bandaging', progress: healProgress(me) ?? 0 };
  } else if (me.digAt) {
    // 66.8: digging in — how far along, on what ground (a scrape dug through deepens into a foxhole)
    timed = { text: `${me.digAt.deepen ? 'digging a foxhole' : 'digging in'} · ${me.digAt.dig.material}`, progress: me.digAt.progress };
  } else if (me.alive && !me.mount) {
    const crate = medical ? crateInReach(state, me) : null;
    const need = crate ? supplyNeeds(me, ps, config) : null;
    const kits = kitsAtCrate(state, me);   // 69.2g's follow-up (a): relays and the beacon too
    if (host.seatOffer) prompt = { key: 'use', verb: host.seatOffer };   // 63.1d's follow-up: "E driver" — what E gets into
    else if (crate && need && (need.bandages > 0 || need.rounds > 0)) prompt = { key: 'use', verb: 'take from the crate', detail: `${crate.supply!.bandages} bandages${crate.supply!.magazines > 0 ? `, ${crate.supply!.magazines} mags` : ''}${kits ? ` · ${kits}` : ''}` };
    else if (kits) prompt = { key: 'use', verb: 'take from the crate', detail: kits };
    else if (medical && host.medicTarget && bandagesLeft(me) > 0) prompt = { key: 'bandage', verb: `bandage ${host.medicTarget.name}` };
    else prompt = buildPrompt(state, me, Math.round(host.time / PHYS_DT));   // 69.2g's follow-up (b, c): its own build task
    // (66.7 (a): no cover prompt in the middle — the owner: "annoying and messing up aim"; Shift just works in reach)
  }

  const keysCard: GroundHudView['keysCard'] = host.commanding ? 'none' : host.helpHeld || host.time - host.heldSince < KEYS_OPEN_S ? 'open' : 'closed';
  return { ...base, arrival, you, hands, timed, prompt, keysCard, digOn: config.entrench === true, ...(me.alive && !me.mount && host.gait ? { gait: host.gait } : {}), crosshair: me.alive ? host.crosshair : 'hidden', cover: me.alive && !me.mount ? coverMark(host.coverHeld ?? null) : null };
}

/**
 * 63.1f's follow-up (the owner in a gun pit: "I can't figure out how to turn the cannon left / right"): seated in a gun pit,
 * its gun's bearing against the pit's front — 'ahead', or '40° right' / '40° left' (the turret turns all the way round);
 * null anywhere else. The seated HUD's traverse row and the controls line read it.
 */
export function pitTraverse(state: Pick<GameState, 'structures'>, me: Pick<PlayerState, 'mount' | 'heading'>): string | null {
  const s = me.mount ? state.structures.find(x => x.id === me.mount!.bodyId) : undefined;
  if (!s || s.type !== 'emplacement' || !me.heading) return null;
  const up = vecNormalize(s.surfaceDir), front = uprightFrame(up).tz, right = rightOf(front, up), h = me.heading;
  const deg = Math.round((Math.atan2(h.x * right.x + h.y * right.y + h.z * right.z, h.x * front.x + h.y * front.y + h.z * front.z) * 180) / Math.PI);
  return Math.abs(deg) < 2 ? 'ahead' : `${Math.abs(deg)}° ${deg > 0 ? 'right' : 'left'}`;
}

/**
 * 67.83: a seat's reload whose whole time the state does not keep (a gun pit's: its shell's, slowed by wear and unspotted fire):
 * the time left when it was first seen, by structure and seat — a reload seen a frame late reads a frame short, nothing more.
 */
const seatReloadStarts = new Map<string, number>();
const share = (left: number, whole: number) => Math.max(0, Math.min(1, whole > 0 ? 1 - left / whole : 1));
/**
 * A mortar carrier's mission rounds still to go, soonest first: each one's fire tick, and the tick its wait began — the call's
 * for the first round (laid MORTAR.laySeconds after it, core/mortar.ts callMortar), a stagger before for the rest (a fired
 * round leaves the mission's list, so the first is told by its tick, not its place).
 */
function mortarRoundsDue(state: GameState, gun: number, tick: number): Array<{ fireTick: number; from: number; first: boolean }> {
  const lay = Math.round(MORTAR.laySeconds / PHYS_DT), stagger = Math.round(MORTAR.staggerSeconds / PHYS_DT);
  const out: Array<{ fireTick: number; from: number; first: boolean }> = [];
  for (const x of state.airstrikes?.inbound ?? []) {
    if (x.kind !== 'mortar') continue;
    for (const r of x.rounds) {
      if (r.gun !== gun || r.fireTick <= tick) continue;
      const first = r.fireTick === x.calledTick + lay;
      out.push({ fireTick: r.fireTick, from: first ? x.calledTick : r.fireTick - stagger, first });
    }
  }
  return out.sort((a, b) => a.fireTick - b.fireTick);
}

/** 69.50 step 1: the story's objectives as the HUD tells them — the viewer's side's first ("Hold B 1:12 / 2:00", "Destroy their air defence 1/2"), then the other's ("They: …"). */
export function objectiveLines(state: GameState, team: number | null): NonNullable<GroundHudView['match']['objectives']> {
  const sites = state.modeState.points?.sites ?? [], list = state.modeState.objectives ?? [];
  const mmss = (s: number) => clockOf(Math.max(0, Math.floor(s)));
  const line = (o: (typeof list)[number]) => {
    const mine = team === null || o.team === team, they = mine ? '' : 'They: ', done = o.doneTick !== undefined;
    const letter = String.fromCharCode(65 + Math.max(0, sites.findIndex(s => s.id === o.pointId)));
    if (o.kind === 'hold') {
      const need = o.holdSeconds ?? STORY.holdSeconds, held = done ? need : o.held ?? 0;
      return { text: `${they}${mine ? 'Hold' : 'hold'} ${letter} ${mmss(held)} / ${mmss(need)}`, progress: Math.min(1, held / need), done, mine };
    }
    if (o.kind === 'take') return { text: `${they}${mine ? 'Take' : 'take'} ${letter}${o.label ? ` (${o.label})` : ''}`, progress: done ? 1 : 0, done, mine };
    // 69.61 step 4: the upload's clock; the intel's whereabouts (the beacon both sides see) and, carried, how near home
    if (o.kind === 'transmit') {
      const need = o.holdSeconds ?? STORY.uploadSeconds, held = done ? need : o.held ?? 0;
      return { text: `${they}${mine ? 'Upload from' : 'upload from'} ${letter} ${mmss(held)} / ${mmss(need)}`, progress: Math.min(1, held / need), done, mine };
    }
    if (o.kind === 'carry') {
      const it = o.item, base = state.board?.bases.find(b => b.team === o.team), from = sites.find(s => s.id === o.pointId);
      const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = it ? state.planets[it.planetIndex]?.radius ?? 1 : 1;
      const out = it && base ? arcDistance(it.surfaceDir, base.dir, R) / person : 0, start = from && base ? arcDistance(from.surfaceDir, base.dir, R) / person : 1;
      const atPoint = !!it && !!from && arcDistance(it.surfaceDir, from.surfaceDir, R) <= STORY.carryReachPeople * person;
      const where = done ? '' : it?.carrierId !== undefined ? ` — carried, ${Math.round(out)} people out` : atPoint ? ` — at ${letter}` : ' — on the ground';
      return { text: `${they}${mine ? 'Carry their' : 'carry your'} ${o.label?.replace(/^the /, '') ?? 'item'} from ${letter} home${where}`, progress: done ? 1 : it?.carrierId !== undefined ? Math.max(0, Math.min(1, 1 - out / Math.max(1, start))) : 0, done, mine };
    }
    // 69.61 step 5: the column — to its own side, how near its head is to its halt (the play's convoy; the jump-off short of
    // the point); to the other, only that one is due
    if (o.kind === 'escort') {
      // (69.80: a column halted by contact has left the objective's watch till it re-forms — its play at the point, still on)
      const play = o.column !== undefined ? state.plays?.find(p => p.id === o.column)
        : state.plays?.find(p => p.team === o.team && 'pointId' in p.objective && p.objective.pointId === o.pointId && p.phase !== 'done' && p.mounted?.convoy?.halted?.how === 'herringbone'), c = play?.mounted?.convoy;
      const lead = c ? state.structures.find(v => v.id === (c.head ?? c.order[0])) : undefined, base = state.board?.bases.find(b => b.team === o.team);
      const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = play ? state.planets[play.position.planetIndex]?.radius ?? 1 : 1;
      const out = lead && c ? arcDistance(lead.surfaceDir, c.halt, R) / person : 0, start = c && base ? arcDistance(base.dir, c.halt, R) / person : 1;
      const contact = c?.halted?.how === 'herringbone', arrived = !!c?.halted && !contact;
      const where = done || !mine ? '' : contact ? ` — halted under fire, ${Math.round(out)} people out` : arrived ? ' — halted, its troops coming up' : c && assembling(c) ? ' — forming up' : lead ? ` — marching, ${Math.round(out)} people out` : ' — waiting for a column';   // (DECISIONS 290 (1): forming at its assembly first)
      return { text: `${they}${mine ? 'Escort' : 'escort'} ${mine ? 'the' : 'a'} column to ${letter}${where}`, progress: done ? 1 : lead && mine ? (arrived ? 1 : Math.max(0, Math.min(1, 1 - out / Math.max(1, start)))) : 0, done, mine };
    }
    const n = o.targets?.length ?? 0, gone = n - targetsLeft(state, o).length;
    return { text: `${they}${mine ? 'Destroy' : 'destroy'} ${mine ? 'their' : 'your'} ${o.label ?? 'targets'} ${gone}/${n}`, progress: n ? gone / n : 0, done, mine };
  };
  return [...list.filter(o => team === null || o.team === team), ...list.filter(o => team !== null && o.team !== team)].map(line);
}

/**
 * 69.61 step 2: the beat when an objective is done (§0.13's beats; the host's note line) — yours *"Done: Hold B · 2 of 3"*,
 * theirs *"They've done: take C (the high ground) · 1 of 3"*, and a side with its all done *"… — all done"*. The objective's
 * own line (objectiveLines) without its clock or count. Null for an objective not in the list.
 */
export function objectiveBeat(state: GameState, objectiveId: number, team: number | null): string | null {
  const list = state.modeState.objectives ?? [], o = list.find(x => x.id === objectiveId);
  if (!o) return null;
  const mine = team === null || o.team === team;
  // (objectiveLines' order: the viewer's side's, then the other's — or the list's own with no viewer)
  const order = team === null ? list : [...list.filter(x => x.team === team), ...list.filter(x => x.team !== team)];
  const line = objectiveLines(state, team)[order.indexOf(o)];
  const text = (line?.text ?? '').replace(/^They: /, '').replace(/ \d+:\d\d \/ \d+:\d\d$| \d+\/\d+$/, '');
  const side = list.filter(x => x.team === o.team), done = side.filter(x => x.doneTick !== undefined).length;
  const count = done >= side.length ? 'all done' : `${done} of ${side.length}`;
  return mine ? `Done: ${text} · ${count}` : `They've done: ${text} · ${count}`;
}

/**
 * 69.61 step 4's follow-up: the carry objectives' items as the view draws them (rendering/objectiveMarkers.ts, `item`) — each
 * not yet delivered, where it is (its carrier's place while carried), the side that carries it, and whether it is carried.
 * The item's place is the beacon both sides see (its HUD line tells it to both), so no fog. Reads state only.
 */
export function carryBeacons(state: GameState): Array<{ objectiveId: number; team: number; planetIndex: number; surfaceDir: Vec3; carried: boolean }> {
  return (state.modeState.objectives ?? []).filter(o => o.kind === 'carry' && o.item && o.doneTick === undefined)
    .map(o => ({ objectiveId: o.id, team: o.team, planetIndex: o.item!.planetIndex, surfaceDir: o.item!.surfaceDir, carried: o.item!.carrierId !== undefined }));
}

/**
 * 69.61 step 3: a consequence's beat (core/consequences.ts) for the viewer's side — the sky opened over their site (told
 * only to the side that knows it), a FOB lost (its side, what went with it; the other side, that it is down), an HQ lost
 * (both sides: the arrivals stop). Null when the viewer is not told.
 */
export function consequenceBeat(e: { kind: 'skyOpened'; team: number } | { kind: 'fobLost'; team: number; taken: boolean } | { kind: 'hqLost'; team: number }, team: number | null): string | null {
  if (e.kind === 'skyOpened') return team === null || e.team === team ? 'The sky over their air-defence site is open — ships can come in there' : null;
  const ours = team === null || e.team === team;
  if (e.kind === 'hqLost') return ours ? 'Our HQ is down — no more arrivals' : 'Their HQ is down — no more arrivals for them';
  if (ours) return e.taken ? 'Our FOB was taken — its stock and its link with it' : 'Our FOB is down — its stock and its link are gone';
  return e.taken ? 'We took their FOB' : 'Their FOB is down';
}

/**
 * 69.61 step 4: a carry's beat (core/storyObjectives.ts `itemCarried`; the host's note line) — the intel taken up, dropped
 * (its carrier down) or returned, told to each side in its own words (`team` the carrying side's).
 */
export function carryBeat(e: { team: number; how: 'taken' | 'dropped' | 'returned' }, team: number | null): string {
  const ours = team === null || e.team === team;
  if (e.how === 'taken') return ours ? 'We have their intel — get it home' : 'They have our intel — stop the carrier';
  if (e.how === 'dropped') return ours ? 'Our carrier is down — the intel is on the ground' : 'Their carrier is down — get our intel back';
  return ours ? 'They returned the intel to its point' : 'We returned our intel';
}

/** 69.80: two carry beats of one item this close together (seconds) are told as one line. */
export const CARRY_BEAT_MERGE_SECONDS = 3;

/**
 * 69.80 (lane H's Story read: "Their carrier is down — get our intel back" and "They have our intel — stop the carrier" in the
 * same second, and the one note line showing whichever came last): a carry's beat told with the one before it, for the same
 * item within CARRY_BEAT_MERGE_SECONDS — the carrier down and the intel taken up again (or returned) in one line. Otherwise
 * the beat alone (carryBeat).
 */
export function carryBeatAfter(prev: { team: number; how: 'taken' | 'dropped' | 'returned'; at: number } | null, e: { team: number; how: 'taken' | 'dropped' | 'returned' }, team: number | null, at: number): string {
  if (!prev || prev.team !== e.team || prev.how !== 'dropped' || at - prev.at > CARRY_BEAT_MERGE_SECONDS || e.how === 'dropped') return carryBeat(e, team);
  const ours = team === null || e.team === team;
  if (e.how === 'taken') return ours ? 'Our carrier is down — another of ours has the intel, get it home' : 'Their carrier is down, but they have our intel again — stop the carrier';
  return ours ? 'Our carrier is down — they returned the intel to its point' : 'Their carrier is down — we returned our intel';
}

/**
 * 69.80 (lane H's Story read: "Hold A 0:55 / 2:00" at minute 9, "0:00" at minute 10, and nothing said): the holds whose clock
 * has run back to 0 since `before` (each hold objective's held seconds, as the host last saw them) — a beat for each, the
 * viewer's ("We lost A — the hold clock reset") or the other side's ("They lost B — their hold clock reset") — and the clocks
 * now. A clock under HOLD_BEAT_FROM_SECONDS that falls back says nothing. Reads state only.
 */
export const HOLD_BEAT_FROM_SECONDS = 5;
export function holdBreaks(state: GameState, before: ReadonlyMap<number, number>, team: number | null): { beats: string[]; held: Map<number, number> } {
  const held = new Map<number, number>(), beats: string[] = [];
  const sites = state.modeState.points?.sites ?? [];
  for (const o of state.modeState.objectives ?? []) {
    if (o.kind !== 'hold' || o.doneTick !== undefined) continue;
    const now = o.held ?? 0, was = before.get(o.id);
    held.set(o.id, now);
    if (was === undefined || was < HOLD_BEAT_FROM_SECONDS || now > 0) continue;
    const letter = String.fromCharCode(65 + Math.max(0, sites.findIndex(s => s.id === o.pointId)));
    beats.push(team === null || o.team === team ? `We lost ${letter} — the hold clock reset` : `They lost ${letter} — their hold clock reset`);
  }
  return { beats, held };
}

/**
 * 69.61 step 5: an escort's beat (core/storyObjectives.ts `columnMarch`; the host's note line) — the column moving out, told
 * to its own side; stopped short (its play over before its halt), told to both. Null when the viewer is not told.
 * 69.80: a stop by contact on the march is a halt, not the end — the herringbone re-forms and marches on (DECISIONS 311), so
 * it is told as one, and the march after it (`after` 'contact': the host's last word on this column) as moving again.
 */
export function columnBeat(e: { team: number; how: 'marching' | 'stopped'; why?: 'contact' | 'over' }, team: number | null, after: 'contact' | null = null): string | null {
  const ours = team === null || e.team === team;
  if (e.how === 'marching') return !ours ? null : after === 'contact' ? 'Our column is moving again — see it through' : 'Our column is moving out — see it through';
  if (e.why === 'contact') return ours ? 'Our column is halted under fire — clear the road' : 'We halted their column';
  return ours ? 'Our column is stopped short — on foot from here' : 'We stopped their column';
}

/** 67.83: each reload under way for `me` — in hand (its own whole time), at its seat (the guns' constants), at the mortar. Reads state only. */
export function reloadsOf(state: GameState, me: PlayerState, time: number): Array<{ label: string; progress: number; doing?: string }> {
  const out: Array<{ label: string; progress: number; doing?: string }> = [];
  if (me.reloadLeft !== undefined && !me.mount) out.push({ label: weaponOf(me), progress: share(me.reloadLeft, me.reloadTime ?? me.reloadLeft) });
  const s = me.mount ? state.structures.find(x => x.id === me.mount!.bodyId) : undefined, seat = s?.seats?.[me.mount!.seat];
  if (!s || !seat) return out;
  const key = `${s.id}/${me.mount!.seat}`;
  if (seat.reloadLeft === undefined) seatReloadStarts.delete(key);
  else if (seat.guns?.length) out.push({ label: seat.guns[0], progress: share(seat.reloadLeft, mountedGunSpec(personSetOf(state), seat.guns[0]).reload) });
  else if (seat.launcher) out.push({ label: seat.launcher === 'rocket' ? 'rockets' : 'missile', progress: share(seat.reloadLeft, MOUNT_LAUNCHERS[seat.launcher].reload) });
  else {
    const start = Math.max(seatReloadStarts.get(key) ?? 0, seat.reloadLeft);
    seatReloadStarts.set(key, start);
    out.push({ label: 'gun', progress: share(seat.reloadLeft, start) });
  }
  if (seat.main?.reloadLeft !== undefined) out.push({ label: 'main gun', progress: share(seat.main.reloadLeft, mainGunReload(state, me)) });   // (69.46: a crewman's CREWMAN.reload)
  if (seat.mortar && s.mortar) {
    // the mortar: setting up from its stop; firing a mission, laying for its first round (from the call), then each next
    // round's stagger (the last rounds of its store too)
    const tick = Math.round(time / PHYS_DT), since = s.mortar.stillSince;
    const next = mortarRoundsDue(state, s.id, tick)[0];
    if (next !== undefined) out.push({ label: 'mortar', doing: next.first ? 'laying' : 'next round', progress: share(next.fireTick - tick, next.fireTick - next.from) });
    else if (s.mortar.rounds > 0 && since !== undefined && (tick - since) * PHYS_DT < mortarSetupSeconds(state, s)) { const set = mortarSetupSeconds(state, s); out.push({ label: 'mortar', doing: 'setting up', progress: share(set - (tick - since) * PHYS_DT, set) }); }
  }
  return out;
}

/** DECISIONS 402: the side's stock for the HUD — low under one issue (STOCK_LOW: a rifleman's 8 magazines, 2 bandages) for each of its people; 406: its explosives beside. */
function sideStock(state: GameState, team: number): { magazines: number; bandages: number; explosives: number; low: boolean } {
  const left = stockLeft(state, team), people = state.players.filter(p => p.teamIndex === team).length || 1;
  return { ...left, explosives: explosivesLeft(state, team), low: left.magazines < STOCK_LOW.magazines * people || left.bandages < STOCK_LOW.bandages * people };
}
const STOCK_LOW = { magazines: 8, bandages: 2 };
